// client/src/components/Analytics/analyticsUtils.js

// -----------------------------
// TIME / FORMAT HELPERS
// -----------------------------
export const formatStudyTime = (totalSeconds) => {
  if (!totalSeconds || isNaN(totalSeconds)) return "0m";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes === 0 && totalSeconds > 0) return "< 1m";
  return `${minutes}m`;
};

export const formatDuration = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  if (s <= 0) return "0m";
  if (s < 60) return `${s}s`;

  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (h <= 0) {
    if (sec === 0) return `${m}m`;
    return `${m}m ${sec}s`;
  }

  if (m === 0 && sec === 0) return `${h}h`;
  if (sec === 0) return `${h}h ${m}m`;
  return `${h}h ${m}m ${sec}s`;
};

export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// -----------------------------
// WEEK HELPERS (THIS WEEK ONLY)
// -----------------------------
export const DAYS_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const DAY_INDEX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

export const getTodayIndexMonBased = () => {
  const js = new Date().getDay(); // Sun=0..Sat=6
  return js === 0 ? 6 : js - 1;   // Mon=0..Sun=6
};

export const getStartOfCurrentWeek = () => {
  const now = new Date();
  const day = now.getDay(); // Sun=0..Sat=6
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);
  return start;
};

export const DEFAULT_WEEK = DAYS_ORDER.map((day) => ({ day, quizSeconds: 0, readSeconds: 0 }));
export const DAILY_GOAL_SECONDS = 3600;

// supports:
// - NEW: { quizSeconds, readSeconds }
// - OLD: { quiz, read } percent (0-100)
export const toSecondsWeekly = (d) => {
  const qs = Number(d?.quizSeconds);
  const rs = Number(d?.readSeconds);

  if (Number.isFinite(qs) || Number.isFinite(rs)) {
    return {
      quizSeconds: Math.max(0, qs || 0),
      readSeconds: Math.max(0, rs || 0),
    };
  }

  const qPct = Number(d?.quiz ?? 0);
  const rPct = Number(d?.read ?? 0);

  const quizSeconds = Math.round((Math.max(0, Math.min(100, qPct)) / 100) * DAILY_GOAL_SECONDS);
  const readSeconds = Math.round((Math.max(0, Math.min(100, rPct)) / 100) * DAILY_GOAL_SECONDS);

  return { quizSeconds, readSeconds };
};

// force to THIS WEEK only (Mon..Sun) and zero future days
export const coerceToThisWeek = (weeklyActivityRaw) => {
  const base = DEFAULT_WEEK.map((d) => ({ ...d }));
  const todayIdx = getTodayIndexMonBased();

  (weeklyActivityRaw || []).forEach((item) => {
    const day = item?.day;
    if (!DAY_INDEX.hasOwnProperty(day)) return;
    const idx = DAY_INDEX[day];
    const secs = toSecondsWeekly(item);
    base[idx].quizSeconds = secs.quizSeconds;
    base[idx].readSeconds = secs.readSeconds;
  });

  // zero-out future days (after today)
  for (let i = todayIdx + 1; i < base.length; i++) {
    base[i].quizSeconds = 0;
    base[i].readSeconds = 0;
  }

  return base;
};

// -----------------------------
// OTHER HELPERS
// -----------------------------
export const getPercentage = (item) => {
  if (typeof item?.percentage === "number") return item.percentage;
  if (
    typeof item?.score === "number" &&
    typeof item?.totalQuestions === "number" &&
    item.totalQuestions > 0
  ) {
    return Math.round((item.score / item.totalQuestions) * 100);
  }
  return null;
};

export const getModeLabel = (mode) => {
  const m = String(mode || "").toLowerCase();
  if (m === "exam") return "Exam Simulation";
  if (m === "practice") return "Practice Quiz";
  if (m === "quiz") return "Quiz";
  return "Quiz";
};

// fallback weekly from results if stats.weeklyActivity missing (last 7 days)
export const buildWeeklyFromHistoryLast7Days = (history) => {
  const week = DEFAULT_WEEK.map((d) => ({ ...d }));
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - 6);
  start.setHours(0, 0, 0, 0);

  const getDayKey = (date) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()];

  (history || []).forEach((h) => {
    const created = new Date(h?.createdAt || "");
    if (isNaN(created.getTime())) return;
    if (created < start || created > now) return;

    const day = getDayKey(created);
    const idx = week.findIndex((w) => w.day === day);
    if (idx === -1) return;

    const secs = Math.max(0, Number(h?.timeSpentSeconds) || 0);
    week[idx].quizSeconds += secs;
  });

  // zero future days
  const todayIdx = getTodayIndexMonBased();
  for (let i = todayIdx + 1; i < week.length; i++) {
    week[i].quizSeconds = 0;
    week[i].readSeconds = 0;
  }

  return week;
};

export const normalizeStats = (statsRes) => {
  if (!statsRes) return null;
  if (typeof statsRes === "object" && statsRes.totalSeconds != null) return statsRes;
  if (typeof statsRes === "object" && statsRes.data && typeof statsRes.data === "object") return statsRes.data;
  return null;
};

export const extractReviewerTitle = (res) => {
  const src = res?.data ?? res;
  return (
    src?.topic ||
    src?.title ||
    src?.name ||
    src?.subject ||
    src?.reviewerTitle ||
    res?.topic ||
    res?.title ||
    ""
  );
};

export const extractReviewerId = (item) => {
  if (!item) return null;
  const r = item.reviewerId;
  if (typeof r === "string" && r.trim()) return r.trim();
  if (r && typeof r === "object") return r._id || r.id || null;
  const direct = item.reviewer;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  if (direct && typeof direct === "object") return direct._id || direct.id || null;
  return null;
};
